'use client';

import { CustomButton } from '@/components/buttons/Buttons';
import EmptyTable from '@/components/empty/EmptyTable';
import AutoCompleteSearch from '@/components/search-bar/AutocompleteSearchBar';
import { Table } from '@/components/table/Tables';
import routes, { routeWithId } from '@/constants/routes';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { useNavigate } from 'react-router-dom';
import { Column } from 'primereact/column';
import type { DataTableRowClickEvent, DataTableStateEvent, DataTableValue } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import { useEffect, useMemo, useState } from 'react';
import StatusTag from './StatusTag';
import { useQuery } from '@tanstack/react-query';
import { BaseResponse, getEmployeeStatusString, X4bEmployeeListItem } from '@/types';
import { axiosInstance } from '@/services/axios.service';
import { copyWith, GenericResponse, ListResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import { DateHelper } from '@/common/DateHelper';
import _ from 'lodash';
import { SortOrder } from 'primereact/api';
import { useTableState } from '@/common/useTableState';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

interface People {
	name: string;
	requestDate: string;
	phoneNumber: string;
	status: string;
}

type Props = {
	people: People[];
	setPeople: React.Dispatch<React.SetStateAction<People[]>>;
};

const AwaitingApprovalPeopleTable = (props: Props) => {
	//const { people, setPeople } = props;
	const [selectedPeoples, setSelectedPeoples] = useState<X4bEmployeeListItem[]>([]);
	//const [bannerMessage, setBannerMessage] = useState<string>('');
	const [search, setSearch] = useState<string>('');

	const isManagerOrAdmin = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.Manager, X4bUserRoleEnum.Admin]);

	const navigate1 = useNavigate();

	const { sort, filter, updateFilter, tableProps } = useTableState({ skip: 0, take: 5, nameEmailFilter: '' });

	const peopleQuery = useQuery({
		queryKey: ['AwaitingApprovalPeopleTable.GetEmployeeList', filter, sort],
		initialData: { Items: [] as X4bEmployeeListItem[], TotalRecords: 0 },
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<X4bEmployeeListItem>>>(
				ENDPOINTS['/api/X4bEmployee/GetEmployeeListForApproval'], {
				Skip: filter.skip,
				Take: filter.take,
				SortField: sort.sortField,
				SortOrder: sort.sortOrder,
				Filters: [{ Name: "NameEmail", Value: filter.nameEmailFilter }]
			}
			);
			return result.data.Data;
		}
	});

	const navigate = (event: DataTableRowClickEvent) => {
		navigate1(routeWithId('/people/:id', event.data.Id));
	};


	const searchFilter = (user: X4bEmployeeListItem, query: string) =>
		user.FullName.toLowerCase().includes(query.toLowerCase()) ||
		user.Email.toLowerCase().includes(query.toLowerCase());

	const handleSearchSelect = (user: X4bEmployeeListItem) => updateFilter({ nameEmailFilter: user.Email });

	const debouncedSetFilter = useMemo(
		() => _.debounce((value: string) => {
			updateFilter({ nameEmailFilter: value });
		}, 500),
		[] // Empty dependency array
	);
	useEffect(() => {
		return () => {
			debouncedSetFilter.cancel(); // Clean up the debounced function
		};
	}, [debouncedSetFilter]);

	const handleSearchChange = (value: string) => {
		setSearch(value);
		debouncedSetFilter(value);
	};

	const handleMultipleApproveRequest = async () => {
		await axiosInstance.post<BaseResponse>(ENDPOINTS["/api/X4bEmployee/ApproveEmployees"], selectedPeoples.map(i => i.Id));
		const bannerMessage = (selectedPeoples.length === 1 ? `The request from ${selectedPeoples[0].FullName} has been approved.` : `${selectedPeoples.length} requests have been approved.`);

		showNotification(bannerMessage, undefined, {
			name: 'Undo',
			handler: () => { },
		});

		// Refresh table and clear selection
		await peopleQuery.refetch();
		setSelectedPeoples([]);
	};

	const handleApproveRequest = async (user: X4bEmployeeListItem) => {
		await axiosInstance.post<BaseResponse>(ENDPOINTS["/api/X4bEmployee/ApproveEmployees"], [user.Id]);

		const bannerMessage = `The request from ${user.FullName} has been approved.`

		showNotification(bannerMessage, undefined, {
			name: 'Undo',
			handler: () => { },
		});

		// Refresh table
		await peopleQuery.refetch();
	};

	const handleMultipleDeclineRequest = async () => {
		await axiosInstance.post<BaseResponse>(ENDPOINTS["/api/X4bEmployee/DeclineEmployees"], selectedPeoples.map(i => i.Id));
		const bannerMessage = (selectedPeoples.length === 1 ? `The request from ${selectedPeoples[0].FullName} has been declined.` : `${selectedPeoples.length} requests have been declined.`);

		showNotification(bannerMessage, undefined, {
			name: 'Undo',
			handler: () => { },
		});

		// Refresh table and clear selection
		await peopleQuery.refetch();
		setSelectedPeoples([]);
	};

	const handleDeclineRequest = async (user: X4bEmployeeListItem) => {
		await axiosInstance.post<BaseResponse>(ENDPOINTS["/api/X4bEmployee/DeclineEmployees"], [user.Id]);

		const bannerMessage = `The request from ${user.FullName} has been declined.`
		showNotification(bannerMessage, undefined, {
			name: 'Undo',
			handler: () => { },
		});

		// Refresh table
		await peopleQuery.refetch();
	};

	// Action Buttons Template
	const actionBodyTemplate = (item: X4bEmployeeListItem) => (
		isManagerOrAdmin ? (
			<div className="flex items-center gap-3">
				<CustomButton
					id="awaiting-approve"
					className="people-action-button-success h-9 normal"
					rounded
					onClick={() => handleApproveRequest(item)}
				>
					Approve
				</CustomButton>
				<CustomButton
					id="awaiting-decline"
					className="people-icon-button-danger w-9 h-9"
					rounded
					onClick={() => handleDeclineRequest(item)}
				>
					<i className="pi pi-times" />
				</CustomButton>
			</div>
		) : null
	);

	const startContent = (
		<div className="flex">
			<AutoCompleteSearch
				suggestions={peopleQuery.data.Items}
				placeholder="Search by name or phone number"
				onSelect={handleSearchSelect}
				onChange={handleSearchChange}
				filterMethod={searchFilter}
				label="FullName"
				description="Phone"
				value={search}
				className="w-96"
			/>

		</div>
	);

	const endContent = selectedPeoples.length ? (
		<div className="flex gap-3">
			<CustomButton
				id="multiple-approve"
				className="people-action-button-success h-10"
				rounded
				onClick={handleMultipleApproveRequest}
			>
				<i className="pi pi-check mr-2" />
				Approve
			</CustomButton>
			<CustomButton
				id="multiple-decline"
				className="people-action-button-danger h-10"
				rounded
				onClick={handleMultipleDeclineRequest}
			>
				<i className="pi pi-times mr-2" />
				Decline
			</CustomButton>
		</div>
	) : null;

	const hasPeople = peopleQuery.data?.TotalRecords > 0;

	return (
		<>
			<div>
				<Toolbar
					pt={{
						root: {
							className: 'people-toolbar mb-6',
						},
					}}
					start={startContent}
					end={endContent}
				/>
				<Table
					size="small"
					value={peopleQuery.data.Items}
					selection={selectedPeoples} // Add this line
					paginator={hasPeople}
					emptyMessage={EmptyTable({
						title: 'No employees yet',
						description: 'You’ll able to see your employees here.',
						src: '/images/svg/Empty State - No Employees.svg',
					})}
					selectionMode={'checkbox'}
					onSelectionChange={(e: DataTableValue) =>
						setSelectedPeoples(e.value)
					}
					dataKey="Id"
					onRowClick={navigate}
					lazy
					totalRecords={peopleQuery.data.TotalRecords}

					removableSort={true}
					rowsPerPageOptions={[5, 10, 25, 50]}
					{...tableProps}
				>
					<Column
						selectionMode="multiple"
						style={{ textAlign: 'center', justifyItems: 'center' }}
					/>
					<Column field="FullName" header="Name" sortable
						headerClassName="tablet:!w-28"
						bodyClassName="tablet:!w-28"
					/>
					<Column field="RequestDate" header="Request sent date" sortable
						headerClassName="tablet:!hidden"
						bodyClassName="tablet:!hidden"
						body={(person: X4bEmployeeListItem) => DateHelper.formatDateTime(person.RequestDate)}
					/>
					<Column field="Phone" header="Phone Number" sortable />
					<Column field="StatusName" header="Status" sortable
						body={(person: X4bEmployeeListItem) => (
							<StatusTag value={getEmployeeStatusString(person.Status)} severity="secondary" />
						)}
					/>
					<Column
						header="Action"
						body={actionBodyTemplate}
						headerClassName="tablet:!w-44"
						bodyClassName="tablet:!w-44"
						style={{ paddingBlock: 0 }}
					/>
				</Table>
			</div>
		</>
	);
};

export default AwaitingApprovalPeopleTable;
